/** America/New_York calendar boundaries shared by accounting exports and ops. */

export const OPEN_PERIOD_EXPORT_MESSAGE =
  'This range includes the current month, which is still open, so it cannot be exported as closed books. The prior month can be selected.'

const ET = 'America/New_York'
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

// Read the calendar in ET without parsing an ET wall clock as host-local time.
function etCalendarParts(date: Date): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ET,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const read = (type: Intl.DateTimeFormatPartTypes) => {
    const value = parts.find(part => part.type === type)?.value
    if (!value || !/^\d+$/.test(value)) {
      throw new Error(`America/New_York calendar is missing ${type}`)
    }
    return Number(value)
  }
  const year = read('year')
  const month = read('month')
  const day = read('day')
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    throw new Error('America/New_York calendar parts are out of range')
  }
  return { year, month, day }
}

function isoDay(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function shiftUtcCalendar(year: number, monthIndex: number, day: number): { year: number; month: number; day: number } {
  const shifted = new Date(Date.UTC(year, monthIndex, day))
  return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1, day: shifted.getUTCDate() }
}

/** ET calendar day of `now`, as YYYY-MM-DD. */
export function etTodayIso(now: Date = new Date()): string {
  const { year, month, day } = etCalendarParts(now)
  return isoDay(year, month, day)
}

/** `days` ET calendar days before `etTodayIso(now)`. `0` is today. */
export function etIsoDaysAgo(days: number, now: Date = new Date()): string {
  const { year, month, day } = etCalendarParts(now)
  const shifted = shiftUtcCalendar(year, month - 1, day - days)
  return isoDay(shifted.year, shifted.month, shifted.day)
}

/** First of the current ET month through today. */
export function etMonthToDate(now: Date = new Date()): { from: string; to: string } {
  const { year, month, day } = etCalendarParts(now)
  return { from: isoDay(year, month, 1), to: isoDay(year, month, day) }
}

/** Monday of the current ET week through today. */
export function etWeekToDate(now: Date = new Date()): { from: string; to: string } {
  const { year, month, day } = etCalendarParts(now)
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay() // 0 = Sunday
  const start = shiftUtcCalendar(year, month - 1, day - (weekday === 0 ? 6 : weekday - 1))
  return { from: isoDay(start.year, start.month, start.day), to: isoDay(year, month, day) }
}

/** First of the current ET calendar quarter through today. */
export function etQuarterToDate(now: Date = new Date()): { from: string; to: string } {
  const { year, month, day } = etCalendarParts(now)
  const firstMonth = Math.floor((month - 1) / 3) * 3 + 1
  return { from: isoDay(year, firstMonth, 1), to: isoDay(year, month, day) }
}

/** Last completed ET calendar quarter, inclusive. */
export function lastFullQuarterRange(now: Date = new Date()): { from: string; to: string } {
  const { year, month } = etCalendarParts(now)
  const quarter = Math.floor((month - 1) / 3)
  const prevYear = quarter === 0 ? year - 1 : year
  const firstMonth = (quarter === 0 ? 3 : quarter - 1) * 3 + 1
  // UTC arithmetic supplies the Gregorian month length without a local-date conversion.
  const lastDay = new Date(Date.UTC(prevYear, firstMonth + 2, 0)).getUTCDate()
  return { from: isoDay(prevYear, firstMonth, 1), to: isoDay(prevYear, firstMonth + 2, lastDay) }
}

/** January 1 of the current ET year through today. */
export function etYearToDate(now: Date = new Date()): { from: string; to: string } {
  const { year, month, day } = etCalendarParts(now)
  return { from: isoDay(year, 1, 1), to: isoDay(year, month, day) }
}

/**
 * First of the month eleven months before the current ET month, through today.
 * That is twelve monthly buckets, including the open month.
 */
export function etTrailing12Months(now: Date = new Date()): { from: string; to: string } {
  const { year, month, day } = etCalendarParts(now)
  const start = shiftUtcCalendar(year, month - 1 - 11, 1)
  return { from: isoDay(start.year, start.month, 1), to: isoDay(year, month, day) }
}

/** Last completed ET calendar month, inclusive. */
export function lastFullMonthRange(now: Date = new Date()): { from: string; to: string } {
  const { year, month } = etCalendarParts(now)
  const prevMonth = month === 1 ? 12 : month - 1
  const prevYear = month === 1 ? year - 1 : year
  // UTC arithmetic supplies the Gregorian month length without a local-date conversion.
  const lastDay = new Date(Date.UTC(prevYear, prevMonth, 0)).getUTCDate()
  return { from: isoDay(prevYear, prevMonth, 1), to: isoDay(prevYear, prevMonth, lastDay) }
}

/** First calendar day of the ET month that has not closed yet. */
export function openMonthStart(now: Date = new Date()): string {
  const { year, month } = etCalendarParts(now)
  return isoDay(year, month, 1)
}

/** An invalid, reversed, current-month, or future range cannot be exported as closed books. */
export function rangeIncludesOpenMonth(from: string, to: string, now: Date = new Date()): boolean {
  if (!ISO_DAY.test(from) || !ISO_DAY.test(to) || from > to) return true
  try {
    return to >= openMonthStart(now)
  } catch {
    return true
  }
}
